import { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { validateSession, logoutSession } from './api';

type SessionCheck = { valid: boolean; hasPinSet: boolean; clientId?: string; userId?: string | null };

type CustomerAuthContextType = {
  isAuthenticated: boolean;
  clientId: string | null;
  userId: string | null;
  sessionToken: string | null;
  loading: boolean;
  hasPinSet: boolean;
  login: () => Promise<SessionCheck>;
  logout: () => Promise<void>;
  checkSession: () => Promise<SessionCheck>;
};

const CustomerAuthContext = createContext<CustomerAuthContextType | undefined>(undefined);

type CustomerAuthProviderProps = {
  children: ReactNode;
};

export const CustomerAuthProvider = ({ children }: CustomerAuthProviderProps) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [clientId, setClientId] = useState<string | null>(null);
  const [userId, setUserId] = useState<string | null>(null);
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [hasPinSet, setHasPinSet] = useState(false);

  // Monotonic id so a slower, stale checkSession response cannot overwrite the
  // state produced by a newer one (e.g. the initial validation started before
  // the activation cookie exists must not clobber a successful login).
  const checkSessionSeqRef = useRef(0);

  const checkSession = async (): Promise<SessionCheck> => {
    const requestId = ++checkSessionSeqRef.current;
    const isStale = () => requestId !== checkSessionSeqRef.current;

    try {
      const response = await validateSession();

      if (response.valid && response.clientId) {
        if (!isStale()) {
          setIsAuthenticated(true);
          setClientId(response.clientId);
          setUserId(response.userId ?? null);
          setSessionToken(response.sessionToken ?? null);
          setHasPinSet(response.hasPinSet ?? false);
          setLoading(false);
        }
        return { valid: !isStale(), hasPinSet: response.hasPinSet ?? false, clientId: response.clientId, userId: response.userId };
      } else {
        if (!isStale()) {
          setIsAuthenticated(false);
          setClientId(null);
          setUserId(null);
          setSessionToken(null);
          setHasPinSet(false);
          setLoading(false);
        }
        return { valid: false, hasPinSet: false };
      }
    } catch {
      if (!isStale()) {
        setIsAuthenticated(false);
        setClientId(null);
        setUserId(null);
        setSessionToken(null);
        setHasPinSet(false);
        setLoading(false);
      }
      return { valid: false, hasPinSet: false };
    }
  };

  const login = (): Promise<SessionCheck> => checkSession();

  const logout = async () => {
    await logoutSession();
    setIsAuthenticated(false);
    setClientId(null);
    setUserId(null);
    setSessionToken(null);
    setHasPinSet(false);
  };

  useEffect(() => {
    checkSession();
  }, []);

  const value: CustomerAuthContextType = {
    isAuthenticated,
    clientId,
    userId,
    sessionToken,
    loading,
    hasPinSet,
    login,
    logout,
    checkSession,
  };

  return <CustomerAuthContext.Provider value={value}>{children}</CustomerAuthContext.Provider>;
};

export const useCustomerAuth = (): CustomerAuthContextType => {
  const context = useContext(CustomerAuthContext);

  if (context === undefined) {
    throw new Error('useCustomerAuth must be used within a CustomerAuthProvider');
  }
  return context;
};
